/**
 * Fine-line glyphs, one per treatment.
 *
 * These stand in for photography and are meant to survive it: even once real
 * photographs exist, a small consistent mark beside each treatment name reads
 * better than eleven cropped thumbnails. Drawn on a 32x32 grid with a single
 * stroke weight so the set feels like one hand.
 */
interface TreatmentGlyphProps {
  slug: string;
}

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
      <g {...STROKE}>{children}</g>
    </svg>
  );
}

/** A leaf - facials. */
function LeafGlyph() {
  return (
    <Glyph>
      <path d="M16 27C16 27 6 21 6 13C6 8 10 5 16 5C22 5 26 8 26 13C26 21 16 27 16 27Z" />
      <path d="M16 27V9" />
      <path d="M16 15L21 11M16 19L11 15" />
    </Glyph>
  );
}

/** Water droplets - deep cleanse. */
function DropletGlyph() {
  return (
    <Glyph>
      <path d="M16 5C16 5 9 14 9 19C9 23 12 26 16 26C20 26 23 23 23 19C23 14 16 5 16 5Z" />
      <path d="M13 19C13 21 14 22 16 22" />
    </Glyph>
  );
}

/** A radiant sun - radiance ritual, LED. */
function SunGlyph() {
  return (
    <Glyph>
      <circle cx="16" cy="16" r="6" />
      <path d="M16 4V7M16 25V28M4 16H7M25 16H28M8 8L10 10M22 22L24 24M24 8L22 10M10 22L8 24" />
    </Glyph>
  );
}

/** Open palms with rising energy - reiki. */
function HandsGlyph() {
  return (
    <Glyph>
      <path d="M7 26C7 20 10 17 13 17V11C13 9.5 15 9.5 15 11V16" />
      <path d="M25 26C25 20 22 17 19 17V11C19 9.5 17 9.5 17 11V16" />
      <path d="M16 4V9M12 6L13 9M20 6L19 9" />
    </Glyph>
  );
}

/** A faceted crystal - crystal therapy. */
function CrystalGlyph() {
  return (
    <Glyph>
      <path d="M16 4L24 13L16 28L8 13L16 4Z" />
      <path d="M8 13H24M16 4V28" />
    </Glyph>
  );
}

/** Crystal within a circle of energy - the combined journey. */
function JourneyGlyph() {
  return (
    <Glyph>
      <circle cx="16" cy="16" r="11" />
      <path d="M16 8L21 15L16 24L11 15L16 8Z" />
      <path d="M11 15H21" />
    </Glyph>
  );
}

/** A head in profile with radiating lines - Indian head massage. */
function HeadGlyph() {
  return (
    <Glyph>
      <path d="M20 26C20 22 22 20 22 15C22 10 19 7 15 7C11 7 9 10 9 14C9 17 10 18 10 21" />
      <path d="M15 12C16.5 12 17 13 17 14" />
      <path d="M25 9L27 7M26 15H28M24 4L25 2" />
    </Glyph>
  );
}

/** A blade at an angle - dermaplaning. */
function BladeGlyph() {
  return (
    <Glyph>
      <path d="M7 25L20 12L25 17L12 30" transform="translate(0,-4)" />
      <path d="M20 12L24 4L28 8L25 17" transform="translate(0,-1)" />
    </Glyph>
  );
}

/** Layers peeling back - chemical peel. */
function PeelGlyph() {
  return (
    <Glyph>
      <path d="M5 20C5 20 9 14 16 14C23 14 27 20 27 20" />
      <path d="M7 25C7 25 11 20 16 20C21 20 25 25 25 25" />
      <path d="M16 14V6M13 9L16 6L19 9" />
    </Glyph>
  );
}

/** A dotted grid - microneedling. */
function NeedleGlyph() {
  return (
    <Glyph>
      <circle cx="16" cy="16" r="10" />
      <path
        d="M12 12H12.01M16 12H16.01M20 12H20.01M12 16H12.01M16 16H16.01M20 16H20.01M12 20H12.01M16 20H16.01M20 20H20.01"
        strokeWidth="2"
      />
    </Glyph>
  );
}

const GLYPHS: Record<string, () => React.ReactElement> = {
  "signature-renewal-facial": LeafGlyph,
  "deep-cleanse-facial": DropletGlyph,
  "radiance-ritual-facial": SunGlyph,
  "reiki-healing": HandsGlyph,
  "crystal-therapy": CrystalGlyph,
  "reiki-crystal-journey": JourneyGlyph,
  "indian-head-massage": HeadGlyph,
  dermaplaning: BladeGlyph,
  "chemical-peel": PeelGlyph,
  microneedling: NeedleGlyph,
  "led-light-therapy": SunGlyph,
};

export function TreatmentGlyph({ slug }: TreatmentGlyphProps) {
  // Any treatment added later without its own mark falls back to the leaf,
  // rather than leaving a gap in the layout.
  const Component = GLYPHS[slug] ?? LeafGlyph;
  return <Component />;
}
